import { z } from 'zod';
import { platformJsonValueSchema } from '@crest/platform-contracts/settings';
import { platformImportResolutionProofSchema } from '@crest/platform-contracts/import-resolution';
import { projectsImportResolutionSchema } from '@crest/module-projects/contracts/import-resolution';
export const timesheetsTimeEntryImportCapability = 'timesheets.time-entry-import.v1';
export const timesheetsTimeEntryImportMappingVersion = 'timesheets-time-entry-normalized-v1';
/** Historical base rehearsal count; live restricted runs bind their positive count in the accepted source plan. */
export const timesheetsTimeEntryImportExpectedWaterProCount = 405;
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const uuid = z.string().uuid();
const sourceId = z.string().trim().min(1).max(500);
const tenantKey = z.string().trim().min(1).max(240);
const timestamp = z.string().datetime({ offset: true });
const sourceTimestamp = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/);
const decimalText = z.string().regex(/^-?\d+(?:\.\d+)?$/);
export const timesheetsLegacyTimeEntrySourceSchema = z.strictObject({
    id: uuid,
    user_id: uuid,
    project_id: uuid.nullable(),
    billing_code: z.enum(['general', 'after_hours', 'emergency', 'biohazard', 'on_call', 'drive_time', 'admin', 'break']),
    clock_in: sourceTimestamp,
    clock_out: sourceTimestamp.nullable(),
    duration_minutes: z.number().int().nullable(),
    clock_in_lat: decimalText.nullable(),
    clock_in_lng: decimalText.nullable(),
    clock_out_lat: decimalText.nullable(),
    clock_out_lng: decimalText.nullable(),
    notes: z.string().nullable(),
    approved_by: uuid.nullable(),
    approved_at: sourceTimestamp.nullable(),
    created_at: sourceTimestamp,
    hazard_pay: z.boolean(),
    source: z.enum(['manual', 'photo_arrival', 'photo_departure', 'geofence', 'backfill']),
    triggering_file_id: uuid.nullable(),
    billing_code_travel_from_project_id: uuid.nullable(),
    billing_code_travel_to_project_id: uuid.nullable(),
    rejected_at: sourceTimestamp.nullable(),
    rejected_by: uuid.nullable(),
    rejection_note: z.string().nullable(),
    entry_type: z.enum(['work', 'break']),
    break_type: z.enum(['lunch', 'rest', 'personal', 'other']).nullable(),
    parent_time_entry_id: uuid.nullable(),
    resume_project_id: uuid.nullable(),
    resume_billing_code: z.enum(['general', 'after_hours', 'emergency', 'biohazard', 'on_call', 'drive_time', 'admin']).nullable(),
    resume_hazard_pay: z.boolean(),
    pay_rate_snapshot: decimalText.nullable(),
    pay_multiplier_snapshot: decimalText.nullable(),
    overtime_multiplier_snapshot: decimalText.nullable(),
    manager_adjustment_reason: z.string().nullable(),
    manager_adjusted_by: uuid.nullable(),
    manager_adjusted_at: sourceTimestamp.nullable(),
    tenant_id: uuid,
    personal_vehicle_mileage: z.boolean(),
    voided_at: sourceTimestamp.nullable(),
    voided_by: uuid.nullable(),
    void_reason: z.string().nullable(),
    long_shift_review_notified_at: sourceTimestamp.nullable(),
    long_shift_review_task_id: uuid.nullable(),
    long_shift_confirmed_at: sourceTimestamp.nullable(),
    long_shift_confirmed_by: uuid.nullable(),
    long_shift_confirmation_note: z.string().nullable(),
    after_hours_callout_bonus_enabled: z.boolean().nullable(),
    after_hours_callout_bonus_amount: decimalText.nullable(),
    after_hours_callout_bonus_minutes: z.number().int().nullable(),
});
export const timesheetsTimeEntryImportRowSchema = z.strictObject({
    sequence: z.number().int().positive().safe(),
    sourceTable: z.literal('time_entries'),
    sourceId,
    sourceSha256: sha256,
    sourceUpdatedAt: sourceTimestamp,
    payload: timesheetsLegacyTimeEntrySourceSchema,
}).superRefine((value, context) => {
    if (value.sourceId !== value.payload.id)
        context.addIssue({ code: 'custom', path: ['sourceId'], message: 'Source ID must equal payload ID.' });
});
export const timesheetsTimeEntryMembershipSourceMappingSchema = z.strictObject({
    sourceTenantExternalKey: tenantKey,
    sourceUserId: uuid,
    membershipSourceExternalKey: sourceId,
});
export const timesheetsTimeEntryImportSnapshotSchema = z.strictObject({
    contractVersion: z.literal(timesheetsTimeEntryImportCapability),
    mappingVersion: z.literal(timesheetsTimeEntryImportMappingVersion),
    sourceSystem: z.literal('crest-crm'),
    sourceTenantId: uuid,
    tenantExternalKey: z.literal('water-pro'),
    targetTenantId: uuid,
    sourceEpoch: z.string().trim().min(1).max(240),
    capturedAt: timestamp,
    sourceSha256: sha256,
    sourceRowCount: z.number().int().positive().safe(),
    membershipSourceMappings: z.array(timesheetsTimeEntryMembershipSourceMappingSchema).max(100_000),
    rows: z.array(z.unknown()).min(1).max(100_000),
}).superRefine((value, context) => {
    if (value.rows.length !== value.sourceRowCount) {
        context.addIssue({ code: 'custom', path: ['rows'], message: 'Source rows must equal the explicitly bound source count.' });
    }
});
export const timesheetsTimeEntryMembershipResolutionSchema = z.strictObject({
    sourceUserId: uuid,
    membershipSourceExternalKey: sourceId,
    sourceTenantExternalKey: tenantKey,
    proof: platformImportResolutionProofSchema,
    verified: z.literal(true),
}).superRefine((value, context) => {
    if (value.membershipSourceExternalKey !== value.proof.sourceId
        || value.sourceTenantExternalKey !== value.proof.sourceTenantExternalKey) {
        context.addIssue({ code: 'custom', path: ['proof'], message: 'Membership proof must bind the mapped source membership.' });
    }
});
export const timesheetsTimeEntryProjectResolutionSchema = z.strictObject({
    sourceProjectId: uuid,
    sourceTenantExternalKey: tenantKey,
    proof: projectsImportResolutionSchema,
    targetReference: z.strictObject({ projectId: uuid, label: z.string().trim().min(1).max(240) }),
    verified: z.literal(true),
}).superRefine((value, context) => {
    if (value.sourceProjectId !== value.proof.sourceId
        || value.targetReference.projectId !== value.proof.targetId) {
        context.addIssue({ code: 'custom', path: ['proof'], message: 'Project proof must bind the source Project and target reference.' });
    }
});
const nullableMembershipResolution = timesheetsTimeEntryMembershipResolutionSchema.nullable();
const nullableProjectResolution = timesheetsTimeEntryProjectResolutionSchema.nullable();
export const timesheetsTimeEntryImportTargetPayloadSchema = z.strictObject({
    membershipId: uuid,
    state: z.enum(['active', 'completed', 'needs_correction']),
    entryKind: z.enum(['work', 'break']),
    billingCode: z.enum(['general', 'after_hours', 'emergency', 'biohazard', 'on_call', 'drive_time', 'admin', 'break']),
    breakType: z.enum(['lunch', 'rest', 'personal', 'other']).nullable(),
    workReferenceType: z.literal('project').nullable(),
    workReferenceId: uuid.nullable(),
    workReferenceLabel: z.string().trim().min(1).max(240).nullable(),
    parentTimeEntryId: uuid.nullable(),
    resumeWorkReferenceType: z.literal('project').nullable(),
    resumeWorkReferenceId: uuid.nullable(),
    resumeWorkReferenceLabel: z.string().trim().min(1).max(240).nullable(),
    resumeBillingCode: z.enum(['general', 'after_hours', 'emergency', 'biohazard', 'on_call', 'drive_time', 'admin']).nullable(),
    notes: z.string().max(2_000).nullable(),
    source: z.literal('import'),
    clockInOccurredAt: sourceTimestamp,
    clockOutOccurredAt: sourceTimestamp.nullable(),
    startCommandId: uuid,
    stopCommandId: uuid.nullable(),
    attentionReason: z.string().max(500).nullable(),
    reviewState: z.enum(['pending', 'approved', 'rejected', 'voided']),
    approvedByMembershipId: uuid.nullable(), approvedAt: sourceTimestamp.nullable(),
    rejectedByMembershipId: uuid.nullable(), rejectedAt: sourceTimestamp.nullable(), rejectionNote: z.string().max(2_000).nullable(),
    voidedByMembershipId: uuid.nullable(), voidedAt: sourceTimestamp.nullable(), voidReason: z.string().max(2_000).nullable(),
    managerAdjustmentReason: z.string().max(2_000).nullable(),
    managerAdjustedByMembershipId: uuid.nullable(), managerAdjustedAt: sourceTimestamp.nullable(),
    revision: z.literal(1), payBasisRevision: z.literal(1),
    createdByMembershipId: uuid, updatedByMembershipId: uuid,
    createdAt: sourceTimestamp, updatedAt: sourceTimestamp,
});
export const timesheetsTimeEntryImportActualTargetSchema = timesheetsTimeEntryImportTargetPayloadSchema.extend({
    clockInReceivedAt: sourceTimestamp,
    clockOutReceivedAt: sourceTimestamp.nullable(),
});
export const timesheetsTimeEntryImportActionSchema = z.strictObject({
    sequence: z.number().int().positive().safe(),
    sourceId, sourceSha256: sha256, sourceUpdatedAt: sourceTimestamp,
    sourceTenantExternalKey: tenantKey, targetTenantId: uuid,
    targetId: uuid, correctionId: uuid, identityStrategy: z.literal('preserved-uuid'),
    payload: timesheetsTimeEntryImportTargetPayloadSchema,
    sourceEvidence: timesheetsLegacyTimeEntrySourceSchema,
    actorResolutions: z.strictObject({
        employee: timesheetsTimeEntryMembershipResolutionSchema,
        approved: nullableMembershipResolution,
        rejected: nullableMembershipResolution,
        voided: nullableMembershipResolution,
        managerAdjusted: nullableMembershipResolution,
    }),
    projectResolution: nullableProjectResolution,
    resumeProjectResolution: nullableProjectResolution,
    parentSourceId: uuid.nullable(),
});
export const timesheetsTimeEntryImportRejectSchema = z.strictObject({
    sequence: z.number().int().positive().safe(), sourceId, sourceSha256: sha256,
    reasonCode: z.enum([
        'schema-invalid', 'source-tenant-mismatch', 'duplicate-source-identity', 'source-after-capture',
        'unresolved-membership', 'unresolved-project', 'unresolved-parent', 'ambiguous-lifecycle',
        'invalid-time-range', 'invalid-break-shape', 'unsupported-location-file', 'unsupported-pay-basis',
        'unsupported-mileage', 'unsupported-long-shift', 'unsupported-callout', 'unsupported-travel',
        'unsupported-hazard', 'unsupported-value', 'conflicting-open-entry', 'unsupported-approved-pay-basis',
    ]),
    fieldPaths: z.array(z.string().trim().min(1).max(240)).min(1).max(30),
});
export const timesheetsTimeEntryImportPlanSchema = z.strictObject({
    contractVersion: z.literal(timesheetsTimeEntryImportCapability),
    mappingVersion: z.literal(timesheetsTimeEntryImportMappingVersion),
    sourceSystem: z.literal('crest-crm'), sourceTenantId: uuid, tenantExternalKey: tenantKey,
    targetTenantId: uuid, sourceEpoch: z.string().trim().min(1).max(240), capturedAt: timestamp,
    sourceSha256: sha256, planSha256: sha256,
    actions: z.array(timesheetsTimeEntryImportActionSchema), rejects: z.array(timesheetsTimeEntryImportRejectSchema),
    counts: z.strictObject({ sourceRows: z.number().int().positive().safe(), plannedRows: z.number().int().nonnegative(), rejectedRows: z.number().int().nonnegative() }),
}).superRefine((value, context) => {
    if (value.actions.length + value.rejects.length !== value.counts.sourceRows
        || value.actions.length !== value.counts.plannedRows || value.rejects.length !== value.counts.rejectedRows) {
        context.addIssue({ code: 'custom', path: ['counts'], message: 'Every source row must be applied or quarantined exactly once.' });
    }
});
export const timesheetsTimeEntryImportExecutionReceiptSchema = z.strictObject({
    sequence: z.number().int().positive().safe(), sourceId, sourceSha256: sha256,
    sourceUpdatedAt: sourceTimestamp, targetId: uuid, outcome: z.enum(['applied', 'skipped']),
    previousTargetSha256: sha256.nullable(), targetSha256: sha256,
    targetCanonical: timesheetsTimeEntryImportActualTargetSchema,
    targetReceiptAt: sourceTimestamp,
    targetReceiptProvenance: z.literal('legacy-import-target-receipt'),
    rollbackToken: platformJsonValueSchema,
});
export const timesheetsTimeEntryImportRollbackReceiptSchema = z.strictObject({
    sequence: z.number().int().positive().safe(),
    outcome: z.enum(['applied', 'already-rolled-back', 'rejected-conflict', 'rejected-state']),
    detailCode: z.string().trim().min(3).max(120), targetSha256: sha256.nullable(),
});
//# sourceMappingURL=timesheets-time-entry-import.contract.js.map